import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import '../../index.css';
import Title from '../../components/Title';

import SelectBox from '../../components/SelectBox';
import useCafe24Auth from '../../hooks/useCafe24Auth';
import { getShopList, signUp } from '../../modules/NetworkFunction';

const SelectShopPage = ({ history }) => {
  const query = window.location.search;
  const params = new URLSearchParams(query);
  const cafe24AuthCode = params.get('code');

  const [multiShopList, setMultiShopList] = useState([]);
  const [selectedShop, setSelectedShop] = useState(
    sessionStorage.getItem('selectedShop')
  );
  const [selectedCafe24Shop, setSelectedCafe24Shop] = useState(null);

  const [makeShopData, setMakeShopData] = useState({
    url: '',
    shopKey: '',
  });

  // 카페24 쇼핑몰 아이디 입력 가능 여부
  const [isInputDisabled, setIsInputDisabled] = useState(false);

  const shopSelectBoxRef = useRef(null);
  const cafe24SelectBoxRef = useRef(null);
  const mallIdInputRef = useRef(null);

  const { setCafe24AccessToken } = useCafe24Auth();
  const accessToken = sessionStorage.getItem('accessToken');
  const redirectURI = window.location.origin + window.location.pathname;

  const handleSelectShop = (option) => {
    sessionStorage.setItem('selectedShop', option);
    setSelectedShop(option);
    if (option === '카페24') {
      setMakeShopData({
        url: '',
        shopKey: '',
      });
    } else {
      sessionStorage.removeItem('mallId');
      sessionStorage.removeItem('accessToken');
      setSelectedCafe24Shop(null);
    }
  };

  const handleLinkCafe24 = () => {
    const mallId = mallIdInputRef.current.value;
    if (!mallId) {
      alert('쇼핑몰 아이디를 입력해 주세요.');
      return;
    }
    sessionStorage.setItem('mallId', mallId);
    setCafe24AccessToken(mallId, redirectURI);
  };

  const editMallId = () => {
    setIsInputDisabled(false);
    setSelectedCafe24Shop(null);
    sessionStorage.removeItem('accessToken');
    mallIdInputRef.current.focus();
  };

  const handleLinkMakeshop = (e) => {
    const { name, value } = e.target;
    setMakeShopData({
      ...makeShopData,
      [name]: value,
    });
  };

  const handleSignUp = () => {
    let userData = JSON.parse(sessionStorage.getItem('data'));
    if (selectedShop === '카페24') {
      localStorage.setItem('shopNo', selectedCafe24Shop.shop_no);
      userData = {
        ...userData,
        mallId: sessionStorage.getItem('mallId'),
        shopNo: Number(selectedCafe24Shop.shop_no),
        cafe24URL: `https://${selectedCafe24Shop.primary_domain}`,
      };
    } else {
      userData = {
        ...userData,
        makeshopURL: makeShopData.url,
        makeshopKey: makeShopData.shopKey,
      };
    }

    signUp(
      userData,
      (v) => {
        history.push('/signup/complete');
      },
      () => {},
      (err) => {
        console.log(err);
        alert('회원가입에 실패했습니다. 다시 시도해주세요.');
      }
    );
  };

  useEffect(() => {
    if (cafe24AuthCode) {
      setCafe24AccessToken(sessionStorage.getItem('mallId'), redirectURI);
      window.history.pushState({}, null, '/signup/select-shop');
    }
  }, [cafe24AuthCode, setCafe24AccessToken, redirectURI]);

  useEffect(() => {
    if (accessToken) {
      getShopList(
        {
          mallId: sessionStorage.getItem('mallId'),
          token: accessToken,
        },
        (shopList) => {
          setMultiShopList(shopList);
        },
        (e) => {
          console.log(e);
          alert(
            '멀티 쇼핑몰 목록을 불러오는데 실패했습니다. 다시 시도해주세요.'
          );
          window.location.reload();
        },
        null
      );

      setIsInputDisabled(true);
    }
  }, [accessToken]);

  const nextBtnDisabled =
    selectedShop === '카페24'
      ? !selectedCafe24Shop
      : !makeShopData.url || !makeShopData.shopKey;

  return (
    <div>
      <Title title="쇼핑몰 선택" />
      <InputContainer>
        <SubTitle>연동할 쇼핑몰을 선택해 주세요.</SubTitle>
        <FlexContainer>
          <SelectBox
            ref={shopSelectBoxRef}
            width={383}
            options={['카페24', '메이크샵']}
            onSelect={handleSelectShop}
            defaultOption={selectedShop}
          />
          {selectedShop && (
            <ModifyButton
              onClick={() => {
                shopSelectBoxRef.current.handleOpen();
              }}
            >
              수정
            </ModifyButton>
          )}
        </FlexContainer>
      </InputContainer>

      {selectedShop === '카페24' && (
        <>
          <InputContainer>
            <SubTitle>쇼핑몰 아이디를 입력해 주세요.</SubTitle>
            <Input
              ref={mallIdInputRef}
              type="text"
              width={330}
              defaultValue={sessionStorage.getItem('mallId') ?? ''}
              disabled={isInputDisabled}
            />
            <LinkButton
              onClick={isInputDisabled ? editMallId : handleLinkCafe24}
            >
              {isInputDisabled ? '수정하기' : '연동하기'}
            </LinkButton>
          </InputContainer>
          {sessionStorage.getItem('mallId') && accessToken && (
            <InputContainer>
              <SubTitle>관리할 쇼핑몰을 선택해 주세요.</SubTitle>
              <FlexContainer>
                <SelectBox
                  ref={cafe24SelectBoxRef}
                  width={383}
                  options={multiShopList.map((shop) => shop.shop_name)}
                  onSelect={(option) => {
                    setSelectedCafe24Shop(
                      multiShopList.find((shop) => shop.shop_name === option)
                    );
                  }}
                />
                {selectedCafe24Shop && (
                  <ModifyButton
                    onClick={() => {
                      cafe24SelectBoxRef.current.handleOpen();
                    }}
                  >
                    수정
                  </ModifyButton>
                )}
              </FlexContainer>
            </InputContainer>
          )}
        </>
      )}

      {selectedShop === '메이크샵' && (
        <>
          <InputContainer>
            <SubTitle>쇼핑몰의 URL을 입력해 주세요.</SubTitle>
            <Input
              type="text"
              name="url"
              width={423}
              value={makeShopData.url}
              onChange={handleLinkMakeshop}
            />
          </InputContainer>

          <InputContainer>
            <SubTitle>쇼핑몰의 상점키를 입력해 주세요.</SubTitle>
            <Input
              type="text"
              name="shopKey"
              width={423}
              value={makeShopData.shopKey}
              onChange={handleLinkMakeshop}
            />
          </InputContainer>
        </>
      )}
      <div className="footer-div">
        <button
          className={nextBtnDisabled ? 'rounded-button-g' : 'rounded-button-y'}
          disabled={nextBtnDisabled}
          onClick={handleSignUp}
        >
          다음
        </button>
      </div>
    </div>
  );
};

export default SelectShopPage;

const InputContainer = styled.div`
  margin-bottom: 42px;
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SubTitle = styled.p`
  font-size: 18px;
  margin-bottom: 18px;
`;

const Input = styled.input`
  height: 38px;
  border: 1px solid #ccc;
  border-radius: 5px;
  padding-left: 12px;
  font-size: 18px;
  width: ${({ width }) => width}px;
`;

const LinkButton = styled.button`
  height: 38px;
  margin-left: 10px;
  padding: 8px 12px;
  color: #fff;
  font-size: 16px;
  border: none;
  border-radius: 5px;
  background-color: #b5b5b5;
  cursor: pointer;
`;

const ModifyButton = styled.button`
  font-size: 14px;
  cursor: pointer;
  color: #b5b5b5;
  border: none;
  background-color: transparent;
  text-decoration: underline;
  text-underline-position: under;
  margin-left: 14px;
`;
